import React, { useState } from "react";
import { Row, Col, Card, message, Divider } from "antd";
import Text from "antd/lib/typography/Text";
import moment from "moment";
import {
  DownloadOutlined,
  RetweetOutlined,
  FileTextOutlined,
  SettingOutlined,
  SafetyCertificateOutlined,
  SyncOutlined,
  CheckOutlined,
} from "@ant-design/icons";

import { platformApi } from "../../../../../api";
import JobOrderModal from "../../../JobOrder/JobOrderModal";

const jobStatuses = [
  "Vehicle Received",
  "Estimation",
  "Mechanic Allocated",
  "Work in Progress",
  "Final Inspection",
  "Ready For Delivery",
  "Delivered",
];

const jobStatusIcons = {
  "Vehicle Received": <DownloadOutlined />,
  Estimation: <RetweetOutlined />,
  "Mechanic Allocated": <FileTextOutlined />,
  "Work in Progress": <SettingOutlined />,
  "Final Inspection": <SafetyCertificateOutlined />, // ✅ valid replacement
  "Ready For Delivery": <SyncOutlined />,
  Delivered: <CheckOutlined />,
};

const AssociatedJobOrder = ({ dataSource }) => {
  const [open, setOpen] = useState(false);
  const [jobData, setJobData] = useState({});

  const getJobOrder = (id) => {
    platformApi
      .get(`/api/jobOrder/${id}`)
      .then((res) => {
        let { data } = res;
        if (data.code === 200 && data.response.code === 200) {
          setJobData(data.response.data);
          setOpen(true);
        } else {
          message.error("Unable to fetch job order details", 2);
        }
      })
      .catch(() => {
        message.error("Error while fetching job order", 2);
      });
  };

  // helper to determine progress step index
  const getCurrentStepIndex = (status) => {
    if (!status) return -1;
    if (["PAID", "Delivered"].includes(status)) return jobStatuses.length - 1;
    return jobStatuses.findIndex(
      (s) => s.toLowerCase() === status.toLowerCase()
    );
  };

  return (
    <div>
      {dataSource &&
        dataSource.length > 0 &&
        dataSource.map((job) => {
          const currentIndex = getCurrentStepIndex(job.jobStatus);

          return (
            <Card
              key={job.id}
              style={{ marginBottom: "15px", cursor: "pointer" }}
              onClick={() => getJobOrder(job.id)}
            >
              <Row gutter={16}>
                <Col span={6}>
                  <Card style={{ width: "220px", textAlign: "center" }}>
                    {job.vehicle?.color?.url ? (
                      <img
                        src={job.vehicle.color.url}
                        alt="vehicle"
                        width="180px"
                      />
                    ) : (
                      <div>No Image</div>
                    )}
                    <div style={{ marginTop: "8px" }}>
                      <Text strong>
                        {job.vehicle?.color?.code} - {job.vehicle?.color?.color}
                      </Text>
                    </div>
                  </Card>
                </Col>

                <Col span={8} style={{ textAlign: "center", paddingTop: "3%" }}>
                  <Row gutter={8}>
                    <Text strong style={{ fontSize: "16px" }}>
                      Job No: {job.jobNo}
                    </Text>
                  </Row>
                  <Row gutter={8}>
                    <Text>
                      Date: {moment(job.dateTime).format("DD-MM-YYYY")}
                    </Text>
                  </Row>
                  <Row gutter={8}>
                    <Text>Service: {job.serviceType}</Text>
                  </Row>
                  <Row gutter={8}>
                    <Text>Kilometers: {job.kms} {job.kms && "KM"}</Text>
                  </Row>
                </Col>

                <Col
                  span={10}
                  style={{ textAlign: "center", paddingTop: "3%" }}
                >
                  <Row gutter={8}>
                    <Text>Status: {job.jobStatus}</Text>
                  </Row>
                  <Row gutter={8}>
                    <Text>Chassis No: {job.vehicle?.chassisNo}</Text>
                  </Row>
                  <Row gutter={8}>
                    <Text>Engine No: {job.vehicle?.engineNo}</Text>
                  </Row>
                  <Row gutter={8}>
                    <Text>Register No: {job.vehicle?.registerNo || "N/A"}</Text>
                  </Row>
                </Col>
              </Row>

              {/* 🔹 Job Progress Tracker */}
              <Divider />
              <div
                style={{
                  marginTop: 30,
                  padding: "0 30px",
                  position: "relative",
                }}
              >
                {/* Background line */}
                <div
                  style={{
                    position: "absolute",
                    top: "20px",
                    left: "30px",
                    right: "30px",
                    height: "3px",
                    backgroundColor: "#d9d9d9",
                    zIndex: 0,
                  }}
                />

                {/* Active progress line */}
                <div
                  style={{
                    position: "absolute",
                    top: "20px",
                    left: "30px",
                    width: `calc(${
                      (currentIndex / (jobStatuses.length - 1)) * 100
                    }% - 30px)`,
                    height: "3px",
                    backgroundColor: "#13c2c2",
                    zIndex: 1,
                    transition: "width 0.3s ease",
                  }}
                />

                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    position: "relative",
                    width: "100%",
                  }}
                >
                  {jobStatuses.map((status, idx) => {
                    const isActive = idx <= currentIndex;

                    return (
                      <div
                        key={status}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          flex: 1,
                          position: "relative",
                          zIndex: 2,
                        }}
                      >
                        {/* icon with background */}
                        <div
                          style={{
                            color: isActive ? "#13c2c2" : "#bfbfbf",
                            fontSize: 24,
                            background: "white",
                            padding: "8px",
                            borderRadius: "50%",
                            border: `2px solid ${
                              isActive ? "#13c2c2" : "#d9d9d9"
                            }`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "42px",
                            height: "42px",
                          }}
                        >
                          {jobStatusIcons[status]}
                        </div>

                        {/* label */}
                        <Text
                          style={{
                            marginTop: 10,
                            color: isActive ? "#13c2c2" : "#999",
                            fontWeight: isActive ? 600 : 400,
                            fontSize: 12,
                            textAlign: "center",
                            lineHeight: "16px",
                            maxWidth: 100,
                          }}
                        >
                          {status}
                        </Text>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Card>
          );
        })}

      {open && (
        <JobOrderModal
          open={open}
          close={() => setOpen(false)}
          data={jobData}
          isFromCustomer={true}
        />
      )}
    </div>
  );
};

export default AssociatedJobOrder;
